import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Check, ChevronDown, ChevronUp, Coins, Pencil, Plus, RotateCcw, Search, Star, Trash2, X,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { apiErrorCode } from '@/api/client'
import { instructors as instructorsApi } from '@/api/endpoints'
import { Alert, Checkbox, EmptyState, Input, Select, Spinner } from '@/components/ui'
import ColumnPicker, { useVisibleColumns } from '@/components/ui/ColumnPicker'
import Pagination from '@/components/ui/Pagination'
import { SortableHeader, useOrdering } from '@/components/ui/SortableHeader'
import InstructorDetailModal from '@/features/instructors/InstructorDetailModal'
import NewInstructorModal from '@/features/instructors/NewInstructorModal'
import { useBootstrap } from '@/hooks/useBootstrap'
import { cn, formatDate, formatMoney, parseIds } from '@/lib/utils'
import { useAuth } from '@/store/auth'
import type { Instructor } from '@/types/api'

export default function InstructorsPage() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const readOnly = !useAuth((s) => s.user)?.can_write
  const { data: codebooks } = useBootstrap()

  const [showDeleted, setShowDeleted] = useState(false)
  const [search, setSearch] = useState('')
  const [query, setQuery] = useState('')
  const [course, setCourse] = useState('')
  const [language, setLanguage] = useState('')
  const [favoritesOnly, setFavoritesOnly] = useState(false)
  const [copied, setCopied] = useState<string | null>(null)
  const [detailId, setDetailId] = useState<number | null>(null)
  const [creating, setCreating] = useState(false)
  const [page, setPage] = useState(1)
  // Sto na stranu preto, že šípky na zmenu poradia potrebujú celý zoznam -
  // pri bežnej škole sa tak zmestí na jednu stranu a nič sa nezablokuje.
  const [pageSize, setPageSize] = useState(100)

  const columns = useMemo(
    () => COLUMNS.map((column) => ({ ...column, label: t(column.labelKey) })),
    [t],
  )
  const { toggle: toggleColumn, isVisible, reset } = useVisibleColumns(
    'l2s:instructorColumns',
    columns,
    DEFAULT_COLUMNS,
  )
  // Predvolené je vlastné poradie (`-priority`) - to, ktoré sa dá nastaviť
  // šípkami. Kliknutie do hlavičky ho dočasne prekryje, tretí klik na ten istý
  // stĺpec sa vráti späť.
  const { ordering, toggle, reset: resetOrdering, isDefault } = useOrdering(
    '-priority',
    'l2s:instructorOrdering',
  )

  /** The code gets pasted into Messenger, so one click beats selecting text. */
  function copyCode(code: string) {
    void navigator.clipboard?.writeText(code)
    setCopied(code)
    window.setTimeout(() => setCopied((c) => (c === code ? null : c)), 1500)
  }

  // Debounced so typing a surname does not fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setQuery(search.trim()), 250)
    return () => clearTimeout(timer)
  }, [search])

  const { data, isLoading } = useQuery({
    queryKey: [
      'instructors', showDeleted, query, course, language, favoritesOnly, ordering,
      page, pageSize,
    ],
    queryFn: () =>
      instructorsApi.list({
        page,
        limit: pageSize,
        ordering,
        include_deleted: showDeleted ? 1 : undefined,
        q: query || undefined,
        course: course || undefined,
        language: language || undefined,
        favorite: favoritesOnly ? true : undefined,
      }),
    placeholderData: keepPreviousData,
  })

  const filtered = Boolean(query || course || language || favoritesOnly)
  const count = data?.count ?? 0
  const pages = Math.max(1, Math.ceil(count / pageSize))
  const singlePage = pages === 1

  function clearFilters() {
    setSearch('')
    setQuery('')
    setCourse('')
    setLanguage('')
    setFavoritesOnly(false)
  }

  /**
   * Šípky prepisujú prioritu podľa **celého odoslaného zoznamu**
   * (`services.set_priority`), takže sa dajú ponúknuť len vtedy, keď je na
   * obrazovke celý a v poradí, ktoré priorita určuje:
   *
   * - pri zapnutom filtri by viditeľné riadky dostali priority 1..N a všetci
   *   skrytí by sa premiešali;
   * - pri zoradení podľa iného stĺpca by sa vlastné poradie prepísalo podľa
   *   odmien či abecedy, teda podľa niečoho úplne iného;
   * - pri viacerých stranách by riadky mimo obrazovky vypadli zo zoznamu
   *   úplne.
   */
  const canReorder = !readOnly && !filtered && isDefault && singlePage

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['instructors'] })

  const toggleFavorite = useMutation({
    mutationFn: (id: number) => instructorsApi.toggleFavorite(id),
    onSuccess: invalidate,
  })
  const setPriority = useMutation({
    mutationFn: (order: number[]) => instructorsApi.setPriority(order),
    onSuccess: invalidate,
  })
  const restore = useMutation({
    mutationFn: (id: number) => instructorsApi.restore(id),
    onSuccess: invalidate,
  })
  const remove = useMutation({
    mutationFn: ({ id, force }: { id: number; force: boolean }) =>
      instructorsApi.remove(id, force),
    onSuccess: invalidate,
  })

  const rows = data?.results ?? []

  async function handleDelete(id: number) {
    if (!window.confirm(t('instructors.deleteConfirm'))) return
    try {
      await remove.mutateAsync({ id, force: false })
    } catch (err) {
      if (apiErrorCode(err) === 'instructor.hasReservations') {
        if (window.confirm(t('instructors.hasReservations'))) {
          await remove.mutateAsync({ id, force: true })
        }
      }
    }
  }

  /** Move a row up/down and persist the whole order (handleSetPriority). */
  function move(index: number, delta: number) {
    const next = [...rows]
    const target = index + delta
    if (target < 0 || target >= next.length) return
    const [item] = next.splice(index, 1)
    next.splice(target, 0, item)
    setPriority.mutate(next.map((i) => i.id))
  }

  const languageBydId = new Map((codebooks?.languages ?? []).map((l) => [l.id, l.key]))
  const courseById = new Map((codebooks?.courses ?? []).map((c) => [c.id, c.key]))

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-lg font-semibold">{t('instructors.title')}</h1>
        <span className="text-sm text-slate-400">{data?.count ?? 0}</span>

        <div className="relative w-72">
          <Search
            size={15}
            className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('instructors.searchPlaceholder')}
            className="pl-7 pr-7"
          />
          {search ? (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
              aria-label={t('global.cancel')}
            >
              <X size={14} />
            </button>
          ) : null}
        </div>

        <Select
          value={course}
          onChange={(e) => setCourse(e.target.value)}
          className="w-auto"
          aria-label={t('instructors.courses')}
        >
          <option value="">{t('instructors.allCourses')}</option>
          {(codebooks?.courses ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.key}
            </option>
          ))}
        </Select>

        <Select
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
          className="w-auto"
          aria-label={t('instructors.languages')}
        >
          <option value="">{t('instructors.allLanguages')}</option>
          {(codebooks?.languages ?? []).map((l) => (
            <option key={l.id} value={l.id}>
              {l.key}
            </option>
          ))}
        </Select>

        <button
          type="button"
          onClick={() => setFavoritesOnly((v) => !v)}
          title={t('instructors.favoritesOnly')}
          className={cn(
            'flex items-center gap-1 rounded border px-2 py-1 text-[12px]',
            favoritesOnly
              ? 'border-amber-400 bg-amber-50 text-amber-800'
              : 'border-slate-300 bg-white text-slate-600 hover:bg-slate-50',
          )}
        >
          <Star
            size={13}
            className={favoritesOnly ? 'fill-amber-400 text-amber-400' : 'text-slate-400'}
          />
          {t('instructors.favoritesOnly')}
        </button>

        {filtered ? (
          <button
            type="button"
            onClick={clearFilters}
            className="text-[12px] text-slate-500 hover:text-slate-800 hover:underline"
          >
            {t('instructors.clearFilters')}
          </button>
        ) : null}

        <Checkbox
          className="ml-auto"
          label={t('instructors.showDeleted')}
          checked={showDeleted}
          onChange={(e) => setShowDeleted(e.target.checked)}
        />

        <ColumnPicker
          columns={columns}
          isVisible={isVisible}
          onToggle={toggleColumn}
          onReset={reset}
        />

        {!readOnly ? (
          <button type="button" className="btn-primary" onClick={() => setCreating(true)}>
            <Plus size={15} />
            {t('instructors.create')}
          </button>
        ) : null}
      </div>

      {!readOnly ? (
        <Alert variant="info">
          {canReorder ? (
            t('instructors.reorderHint')
          ) : (
            <span className="flex flex-wrap items-center gap-2">
              {filtered
                ? t('instructors.reorderDisabled')
                : !singlePage
                  ? t('instructors.reorderPaged')
                  : t('instructors.reorderSorted')}
              {!isDefault ? (
                <button
                  type="button"
                  className="font-medium text-brand-700 underline"
                  onClick={resetOrdering}
                >
                  {t('instructors.backToManual')}
                </button>
              ) : null}
            </span>
          )}
        </Alert>
      ) : null}

      <div className="card overflow-x-auto">
        {isLoading ? (
          <div className="flex h-40 items-center justify-center text-slate-400">
            <Spinner className="h-5 w-5" />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState>{filtered ? t('instructors.noMatches') : t('app.empty')}</EmptyState>
        ) : (
          <div className="overflow-x-auto">
            <table className="table-base">
              <thead>
                <tr>
                  <th className="w-16" />
                  {columns
                    .filter((column) => isVisible(column.key))
                    .map((column) => (
                      <SortableHeader
                        key={column.key}
                        field={'sort' in column ? column.sort : undefined}
                        label={column.label}
                        ordering={ordering}
                        onSort={toggle}
                        align={NUMERIC.has(column.key) ? 'right' : 'left'}
                      />
                    ))}
                  <th className="w-28">{t('global.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((instructor, index) => (
                  <tr key={instructor.id} className={cn(instructor.deleted && 'opacity-50')}>
                    <td>
                      {canReorder && !instructor.deleted ? (
                        <div className="flex gap-0.5">
                          <button
                            type="button"
                            className="btn-ghost p-0.5"
                            onClick={() => move(index, -1)}
                            disabled={index === 0}
                          >
                            <ChevronUp size={14} />
                          </button>
                          <button
                            type="button"
                            className="btn-ghost p-0.5"
                            onClick={() => move(index, 1)}
                            disabled={index === rows.length - 1}
                          >
                            <ChevronDown size={14} />
                          </button>
                        </div>
                      ) : null}
                    </td>

                    {columns
                      .filter((column) => isVisible(column.key))
                      .map((column) => (
                        <td
                          key={column.key}
                          className={cn(
                            'whitespace-nowrap',
                            NUMERIC.has(column.key) && 'text-right tabular-nums',
                          )}
                        >
                          {renderCell(column.key, instructor, {
                            readOnly,
                            copied,
                            onCopy: copyCode,
                            onOpen: () => setDetailId(instructor.id),
                            onToggleFavorite: () => toggleFavorite.mutate(instructor.id),
                            languageBydId,
                            courseById,
                            t,
                          })}
                        </td>
                      ))}

                    <td>
                      <div className="flex gap-0.5">
                        {!readOnly ? (
                          <button
                            type="button"
                            className="btn-ghost p-1"
                            title={t('global.edit')}
                            onClick={() => setDetailId(instructor.id)}
                          >
                            <Pencil size={14} />
                          </button>
                        ) : null}
                        <Link
                          to={`/rewards/${instructor.id}`}
                          className="btn-ghost p-1"
                          title={t('rewards.title')}
                        >
                          <Coins size={14} />
                        </Link>
                        {!readOnly ? (
                          instructor.deleted ? (
                            <button
                              type="button"
                              className="btn-ghost p-1"
                              title={t('instructors.restore')}
                              onClick={() => restore.mutate(instructor.id)}
                            >
                              <RotateCcw size={14} />
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="btn-ghost p-1 text-red-500"
                              title={t('global.delete')}
                              onClick={() => void handleDelete(instructor.id)}
                            >
                              <Trash2 size={14} />
                            </button>
                          )
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Pagination
          page={page}
          pages={pages}
          pageSize={pageSize}
          count={count}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size)
            setPage(1)
          }}
        />
      </div>

      {creating ? (
        <NewInstructorModal
          onClose={() => setCreating(false)}
          onCreated={(instructor) => setDetailId(instructor.id)}
        />
      ) : null}

      {detailId ? (
        <InstructorDetailModal
          instructorId={detailId}
          onClose={() => setDetailId(null)}
        />
      ) : null}
    </div>
  )
}

/** `sort` je názov poľa v API (`ordering_fields` v InstructorViewSet).
 *  Jazyky a kurzy sú zoznam id v jednom stĺpci, takže sa zoradiť nedajú. */
const COLUMNS = [
  { key: 'name', labelKey: 'reservation.instructor', sort: 'sname', fixed: true },
  { key: 'messenger', labelKey: 'instructors.messenger' },
  { key: 'tel', labelKey: 'reservation.phone', sort: 'tel' },
  { key: 'email', labelKey: 'reservation.email', sort: 'email' },
  { key: 'languages', labelKey: 'instructors.languages' },
  { key: 'courses', labelKey: 'instructors.courses' },
  { key: 'note', labelKey: 'global.note' },
  { key: 'count', labelKey: 'clients.reservationsCount', sort: 'reservations_count' },
  { key: 'last', labelKey: 'clients.lastReservation', sort: 'last_reservation' },
  { key: 'rewards', labelKey: 'rewards.title', sort: 'rewards_total' },
  { key: 'priority', labelKey: 'instructors.priority', sort: 'priority' },
  { key: 'created', labelKey: 'clients.created', sort: 'created' },
] as const

type ColumnKey = (typeof COLUMNS)[number]['key']

const DEFAULT_COLUMNS: ColumnKey[] = [
  'name', 'messenger', 'tel', 'languages', 'courses', 'count', 'rewards',
]

const NUMERIC: ReadonlySet<ColumnKey> = new Set(['count', 'rewards', 'priority'])

interface CellContext {
  readOnly: boolean
  copied: string | null
  onCopy: (code: string) => void
  onOpen: () => void
  onToggleFavorite: () => void
  languageBydId: Map<number, string | null>
  courseById: Map<number, string | null>
  t: (key: string) => string
}

function renderCell(key: ColumnKey, instructor: Instructor, ctx: CellContext) {
  switch (key) {
    case 'name':
      return (
        <span className="font-medium">
          <button
            type="button"
            className="mr-1.5 align-middle"
            disabled={ctx.readOnly}
            onClick={ctx.onToggleFavorite}
            title={ctx.t('instructors.favorite')}
          >
            <Star
              size={14}
              className={
                instructor.favorite
                  ? 'fill-amber-400 text-amber-400'
                  : 'text-slate-300 hover:text-amber-400'
              }
            />
          </button>
          <button type="button" className="text-brand-600 hover:underline" onClick={ctx.onOpen}>
            {instructor.full_name || `#${instructor.id}`}
          </button>
        </span>
      )

    case 'messenger':
      // V zozname stačí, či prepojený je - odkaz na prepojenie sa vydáva až
      // v detaile, aby token nevznikal ľuďom, ktorých nikto neotvoril.
      return instructor.messenger_psid ? (
        <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
          <Check size={13} />
          {ctx.t('instructors.messengerLinked')}
        </span>
      ) : (
        <span className="text-xs text-slate-400">—</span>
      )

    case 'tel':
      return instructor.tel || '—'

    case 'email':
      return <span className="text-slate-500">{instructor.email || '—'}</span>

    case 'languages':
      return (
        <span className="text-xs text-slate-500">
          {parseIds(instructor.idslang)
            .map((id) => ctx.languageBydId.get(id))
            .filter(Boolean)
            .join(', ') || '—'}
        </span>
      )

    case 'courses':
      return (
        <span className="text-xs text-slate-500">
          {parseIds(instructor.idscourses)
            .map((id) => ctx.courseById.get(id))
            .filter(Boolean)
            .join(', ') || '—'}
        </span>
      )

    case 'note':
      return instructor.note ? (
        <span className="block max-w-[18rem] truncate text-xs text-slate-500" title={instructor.note}>
          {instructor.note}
        </span>
      ) : (
        '—'
      )

    case 'count':
      return instructor.reservations_count ?? 0

    case 'last':
      return formatDate(instructor.last_reservation ?? null) || '—'

    case 'rewards':
      return formatMoney(instructor.rewards_total)

    case 'priority':
      return instructor.priority

    case 'created':
      return <span className="text-xs text-slate-400">{formatDate(instructor.created) || '—'}</span>
  }
}
